import React, { useEffect, useMemo, useState, useRef } from "react";
import * as d3 from "d3";
import ChartLegend from "./chartLegend";
import {
  getTimeAxisTicks,
  getLinearAxisTickCount,
} from "./d3ChartTickUtilities";
import {
  buildQualityAttributeNameLookup,
  formatReportedPriceTooltip,
} from "./tooltipFormatters";
import {
  appendCircleSeries,
  appendVerticalAreaGradient,
  buildPeriodAverageSeries,
  createTooltipHandlers,
  drawTimePriceAxes,
  drawTimePriceGrid,
  initializeChartCanvas,
  removeTooltip,
} from "./d3ChartShared";
import {
  getNormalizedDateRange,
  parseDateOrNull,
} from "../../utils/dateUtils";
import { toFiniteNumber } from "../../services/utilities";
import "./d3-charts.css";

const buildMovingAverage = (rows, windowSize = 3) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  return rows.map((row, index, array) => {
    const start = Math.max(0, index - windowSize + 1);
    const subset = array.slice(start, index + 1).map((x) => x.price);
    const movingAverage =
      subset.reduce((sum, current) => sum + current, 0) / subset.length;

    return { ...row, movingAverage };
  });
};

const getDisplayPrice = (mark) => {
  const explicitPrice = toFiniteNumber(mark?.price);
  if (explicitPrice !== null) return explicitPrice;

  const bid = toFiniteNumber(mark?.bid);
  const offer = toFiniteNumber(mark?.offer);
  if (bid === null || offer === null) return null;

  return (bid + offer) / 2;
};

const LineChart = ({
  data,
  forwardReportingPeriodicity,
  startDate,
  endDate,
  yMin,
  yMax,
  currencySymbol,
  pricePrecision,
  qualityAttributes,
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const qualityAttributeNameLookup = useMemo(
    () => buildQualityAttributeNameLookup(qualityAttributes),
    [qualityAttributes]
  );

  useEffect(() => {
    const chartContext = initializeChartCanvas({
      svgRef,
      tooltipRef,
      setDimensions,
    });
    if (!chartContext) return undefined;

    const { chartSvg, innerWidth, innerHeight } = chartContext;

    const { normalizedStartDate, normalizedEndDate } = getNormalizedDateRange(
      startDate,
      endDate,
      new Date(),
    );
    const safeData = Array.isArray(data) ? data : [];
    const chartData = safeData
      .map((mark) => {
        const rowDate = mark.date ?? mark.marketDate;
        const price = toFiniteNumber(mark.price);
        const bid = toFiniteNumber(mark.bid);
        const offer = toFiniteNumber(mark.offer);
        const displayPrice = getDisplayPrice({ price, bid, offer });

        return {
          ...mark,
          price,
          bid,
          offer,
          date: rowDate,
          chartDate: parseDateOrNull(rowDate),
          displayPrice,
        };
      })
      .filter((mark) => mark.chartDate);

    let startMonth = new Date(normalizedStartDate);
    startMonth.setDate(1);

    let startYear = new Date(normalizedStartDate);
    startYear.setMonth(0);
    startYear.setDate(1);

    const dateSequence = (() => {
      switch (forwardReportingPeriodicity) {
        case "Hour":
          return d3.timeHours(
            d3.timeDay.floor(normalizedStartDate),
            d3.timeDay.ceil(normalizedEndDate),
          );
        case "Day":
          return d3.timeDays(
            d3.timeDay.offset(new Date(normalizedStartDate), -1),
            d3.timeDay.ceil(new Date(normalizedEndDate)),
          );
        case "Week":
          return d3.timeWeeks(
            d3.timeDay.offset(new Date(normalizedStartDate), -1),
            d3.timeDay.ceil(new Date(normalizedEndDate)),
          );
        case "Month":
          return d3.timeMonths(
            d3.timeDay.offset(new Date(startMonth), -1),
            d3.timeDay.ceil(new Date(normalizedEndDate)),
          );
        case "Year":
          return d3.timeYears(
            d3.timeDay.offset(new Date(startYear), -1),
            d3.timeDay.ceil(new Date(normalizedEndDate)),
          );
        default:
          return d3.timeMonths(
            d3.timeDay.offset(new Date(normalizedStartDate), -1),
            d3.timeDay.ceil(new Date(normalizedEndDate)),
          );
      }
    })();

    const trendData = buildPeriodAverageSeries({
      rows: chartData,
      valueAccessor: (mark) => mark.displayPrice,
    });
    const movingAverageData = buildMovingAverage(
      trendData,
      Math.min(3, trendData.length),
    );

    const domainDates =
      dateSequence.length > 0
        ? dateSequence
        : trendData.map((d) => d.chartDate);
    if (domainDates.length === 0) return undefined;

    const xDomain = d3.extent(domainDates);

    const valueSet = [
      ...trendData.map((d) => d.price),
      ...movingAverageData.map((d) => d.movingAverage),
      ...safeData.map((d) => d.price),
      ...safeData.map((d) => d.bid),
      ...safeData.map((d) => d.offer),
    ].filter((value) => value != null && !isNaN(value));

    const autoMin = valueSet.length > 0 ? Math.min(...valueSet) : 0;
    const autoMax = valueSet.length > 0 ? Math.max(...valueSet) : 0;

    const domainMin = yMin ?? autoMin;
    const domainMax = yMax ?? autoMax;

    const yPadding = (domainMax - domainMin || 1) * 0.06;
    const yDomain = [domainMin - yPadding, domainMax + yPadding];

    const xScale = d3.scaleTime().domain(xDomain).range([0, innerWidth]);

    const yScale = d3.scaleLinear().domain(yDomain).range([innerHeight, 0]);

    const tickFormat = (() => {
      switch (forwardReportingPeriodicity) {
        case "Hour":
          return "%a %H:%M";
        case "Day":
          return "%b-%d";
        case "Week":
          return "%Y-%V";
        case "Month":
          return "%b-%y";
        case "Year":
          return "%Y";
        default:
          return "%b-%d";
      }
    })();

    const tickValues = getTimeAxisTicks(domainDates, innerWidth, 80);
    const maxYTicks = getLinearAxisTickCount(innerHeight, 40);

    drawTimePriceGrid({
      chartSvg,
      xScale,
      yScale,
      tickValues,
      maxYTicks,
      innerWidth,
      innerHeight,
    });

    const gradientId = appendVerticalAreaGradient({
      chartSvg,
      idPrefix: "d3-line-area-primary",
      color: "var(--theme-chart-accent-blue)",
      stops: [
        { offset: "0%", opacity: 0.52 },
        { offset: "46%", opacity: 0.22 },
        { offset: "100%", opacity: 0 },
      ],
    });

    const movingAverageLine = d3
      .line()
      .defined((d) => d.movingAverage != null)
      .x((d) => xScale(d.chartDate))
      .y((d) => yScale(d.movingAverage))
      .curve(d3.curveMonotoneX);
    const hasMovingAverageLine = movingAverageData.length > 1;
    const areaData = movingAverageData;
    const areaYValue = (d) => d.movingAverage;

    const area = d3
      .area()
      .defined((d) => areaYValue(d) != null)
      .x((d) => xScale(d.chartDate))
      .y0(innerHeight)
      .y1((d) => yScale(areaYValue(d)))
      .curve(d3.curveMonotoneX);

    if (areaData.length > 1) {
      chartSvg
        .append("path")
        .datum(areaData)
        .attr("class", "d3-chart-area d3-chart-area-primary")
        .attr("fill", `url(#${gradientId})`)
        .attr("d", area);
    }

    if (hasMovingAverageLine) {
      chartSvg
        .append("path")
        .datum(movingAverageData)
        .attr("class", "d3-chart-line d3-chart-line-moving-average")
        .attr("d", movingAverageLine);
    }

    const { showTooltip, hideTooltip, moveTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    if (hasMovingAverageLine) {
      const movingAverageDateBisector = d3.bisector((d) => d.chartDate).center;

      const getNearestMovingAveragePoint = (event) => {
        const [pointerX] = d3.pointer(event, chartSvg.node());
        const hoveredDate = xScale.invert(pointerX);
        const nearestIndex = movingAverageDateBisector(
          movingAverageData,
          hoveredDate,
        );
        const clampedIndex = Math.max(
          0,
          Math.min(nearestIndex, movingAverageData.length - 1),
        );

        return movingAverageData[clampedIndex];
      };

      const showMovingAverageTooltipAtPointer = (event) => {
        const nearestPoint = getNearestMovingAveragePoint(event);
        if (!nearestPoint) return;

        showTooltip(
          event,
          `Moving Average: <strong>${currencySymbol}${nearestPoint.movingAverage.toFixed(
            pricePrecision,
          )}</strong>`,
        );
      };

      chartSvg
        .append("path")
        .datum(movingAverageData)
        .attr("class", "d3-chart-line-hover-target")
        .attr("d", movingAverageLine)
        .on("mouseover", showMovingAverageTooltipAtPointer)
        .on("mousemove", showMovingAverageTooltipAtPointer)
        .on("mouseout", hideTooltip);
    }

    const approvedData = chartData.filter(
      (mark) => mark.displayPrice != null && mark.markState === "Approved",
    );
    const submittedData = chartData.filter(
      (mark) => mark.displayPrice != null && mark.markState === "Submitted",
    );
    const bidData = chartData.filter((mark) => mark.bid != null);
    const offerData = chartData.filter((mark) => mark.offer != null);

    appendCircleSeries({
      chartSvg,
      rows: approvedData,
      className:
        "d3-chart-circle d3-chart-circle-price-approved d3-chart-circle-forward-price",
      yAccessor: (d) => d.displayPrice,
      radius: 2.5,
      tooltipAccessor: (d) =>
        formatReportedPriceTooltip(
          { ...d, price: d.displayPrice },
          currencySymbol,
          pricePrecision,
          qualityAttributeNameLookup
        ),
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    appendCircleSeries({
      chartSvg,
      rows: submittedData,
      className: "d3-chart-circle d3-chart-circle-price-submitted",
      yAccessor: (d) => d.displayPrice,
      radius: 2.35,
      tooltipAccessor: (d) =>
        `<strong>${currencySymbol}${d.displayPrice.toFixed(pricePrecision)}</strong>`,
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    appendCircleSeries({
      chartSvg,
      rows: bidData,
      className: "d3-chart-circle d3-chart-circle-bid",
      yAccessor: (d) => d.bid,
      radius: 1.6,
      tooltipAccessor: (d) =>
        `Bid: <strong>${currencySymbol}${d.bid.toFixed(pricePrecision)}</strong>`,
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    appendCircleSeries({
      chartSvg,
      rows: offerData,
      className: "d3-chart-circle d3-chart-circle-offer",
      yAccessor: (d) => d.offer,
      radius: 1.6,
      tooltipAccessor: (d) =>
        `Offer: <strong>${currencySymbol}${d.offer.toFixed(pricePrecision)}</strong>`,
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    drawTimePriceAxes({
      chartSvg,
      xScale,
      yScale,
      tickValues,
      tickFormat,
      maxYTicks,
      currencySymbol,
      pricePrecision,
      innerHeight,
    });

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [
    data,
    currencySymbol,
    endDate,
    forwardReportingPeriodicity,
    pricePrecision,
    startDate,
    yMax,
    yMin,
    qualityAttributeNameLookup,
  ]);

  return (
    <div className="d3-chart-container">
      <ChartLegend data={data} chartType="line" />
      <svg ref={svgRef} width={dimensions.width} height={dimensions.height}>
        {/* Chart will be drawn here */}
      </svg>
    </div>
  );
};

export default LineChart;
